import React, { useState ,useEffect} from 'react';
import {
    Box, Text, Collapse, useDisclosure, Spinner, VStack, HStack, Icon,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
    Image, useToast, Flex, IconButton, Skeleton, Badge
} from '@chakra-ui/react';
import { 
    FaCalendarAlt, FaChevronDown, FaChevronUp, FaFileContract, FaFileInvoice, 
    FaDownload, FaEye, FaCheck, FaHistory, FaFilePdf, FaImage
} from 'react-icons/fa';
import { getContractHistory, getFilesList, getFileAsBlob } from '../../Services/api';
import { useIsMobile } from '../../Hooks';

const FileViewerModal = ({ isOpen, onClose, fileId, fileMimeType, title }) => {
    const [objectUrl, setObjectUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (isOpen && fileId) {
            setIsLoading(true);
            getFileAsBlob(fileId, 'View')
                .then(response => setObjectUrl(URL.createObjectURL(response.data)))
                .catch(() => {})
                .finally(() => setIsLoading(false));
        }
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [isOpen, fileId]);

    const isImage = fileMimeType?.toLowerCase().startsWith('image/');
    const isPdf = fileMimeType?.toLowerCase() === 'application/pdf';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
            <ModalOverlay />
            <ModalContent sx={{ 
                bg: 'var(--modal-bg)', 
                color: 'var(--modal-text)',
                borderRadius: '2xl'
            }}>
                <ModalHeader sx={{ 
                    bg: 'var(--modal-header-bg)',
                    borderBottom: '1px solid var(--modal-border)'
                }}>
                    {title}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody textAlign="center" p={6}>
                    {isLoading ? (
                        <VStack spacing={4}>
                            <Spinner size="xl" sx={{ color: 'var(--primary)' }} />
                            <Text sx={{ color: 'var(--text-secondary)' }}>Loading preview...</Text>
                        </VStack>
                    ) : (
                        isImage ? <Image src={objectUrl} alt="Preview" maxH="70vh" borderRadius="lg" /> :
                        isPdf ? <Box as="iframe" src={objectUrl} width="100%" height="70vh" borderRadius="lg" title="PDF" /> :
                        <Text sx={{ color: 'var(--text-secondary)' }}>Preview not available for this file type.</Text>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

const FileItem = ({ file }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isDownloading, setIsDownloading] = useState(false);
    const toast = useToast();
    const isMobile = useIsMobile();

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const response = await getFileAsBlob(file.FileId, 'Download');
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.FileName || 'download');
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                if (link.parentNode) link.parentNode.removeChild(link);
            }, 100);
        } catch {
            toast({ 
                title: 'Download failed.', 
                status: 'error',
                position: 'top-right'
            });
        } finally {
            setIsDownloading(false);
        }
    };

    const getFileIcon = () => {
        if (file.FileMimeType?.toLowerCase().startsWith('image/')) return FaImage;
        if (file.FileMimeType?.toLowerCase() === 'application/pdf') return FaFilePdf;
        return file.FileUploadType === 'Contract' ? FaFileContract : FaFileInvoice;
    };

    const getFileColor = () => {
        if (file.FileMimeType?.toLowerCase().startsWith('image/')) return 'var(--file-image-color)';
        if (file.FileMimeType?.toLowerCase() === 'application/pdf') return 'var(--file-pdf-color)';
        return file.FileUploadType === 'Contract' ? 'var(--file-contract-color)' : 'var(--file-invoice-color)';
    };

    const fileIcon = getFileIcon();
    const fileColor = getFileColor();
    const modalTitle = `${file.FileUploadType} - ${file.OriginalName}`;
    const isPreviewable = file.FileMimeType?.toLowerCase().startsWith('image/') || 
                         (file.FileMimeType?.toLowerCase() === 'application/pdf' && !isMobile);

    return (
        <>
            <Flex 
                justify="space-between" 
                align="center" 
                w="full" 
                p={4}
                borderRadius="xl"
                sx={{ 
                    bg: 'var(--file-item-bg)',
                    border: '1px solid var(--file-item-border)',
                    transition: 'all 0.2s ease-in-out',
                    _hover: {
                        bg: 'var(--file-item-hover-bg)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px var(--file-item-shadow)',
                        borderColor: 'var(--file-item-hover-border)'
                    }
                }}
            >
                <HStack flex="1" minW={0} spacing={3}>
                    <Flex
                        w={10}
                        h={10}
                        borderRadius="lg"
                        align="center"
                        justify="center"
                        sx={{
                            bg: fileColor,
                            color: 'white'
                        }}
                    >
                        <Icon as={fileIcon} fontSize="md" />
                    </Flex>
                    <VStack align="start" spacing={0} flex={1} minW={0}>
                        <Text 
                            fontSize="sm" 
                            fontWeight="600" 
                            isTruncated 
                            w="full"
                            sx={{ color: 'var(--file-item-text)' }}
                        >
                            {file.OriginalName}
                        </Text>
                        <Text 
                            fontSize="xs" 
                            sx={{ color: 'var(--file-item-subtext)' }}
                        >
                            {file.FileUploadType} • {new Date(file.CreatedDate).toLocaleDateString()}
                        </Text>
                    </VStack>
                </HStack>
                <HStack spacing={1}>
                    {isPreviewable && (
                        <IconButton
                            size="sm"
                            aria-label="View File"
                            icon={<Icon as={FaEye} />}
                            variant="ghost"
                            onClick={onOpen}
                            sx={{ 
                                color: 'var(--file-action-color)',
                                _hover: {
                                    bg: 'var(--file-action-hover-bg)',
                                    color: 'var(--file-action-hover-color)'
                                }
                            }}
                        />
                    )}
                    <IconButton
                        size="sm"
                        aria-label="Download File"
                        icon={<Icon as={FaDownload} />}
                        variant="ghost"
                        onClick={handleDownload}
                        isLoading={isDownloading}
                        sx={{ 
                            color: 'var(--file-action-color)',
                            _hover: {
                                bg: 'var(--file-action-hover-bg)',
                                color: 'var(--file-action-hover-color)'
                            }
                        }}
                    />
                </HStack>
            </Flex>
            {isPreviewable && (
                <FileViewerModal 
                    isOpen={isOpen} 
                    onClose={onClose} 
                    fileId={file.FileId} 
                    fileMimeType={file.FileMimeType}
                    title={modalTitle}
                />
            )}
        </>
    );
};

const HistoryItem = ({ item }) => {
    const { isOpen, onToggle } = useDisclosure();
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const handleToggle = () => {
        if (!isOpen && files.length === 0) {
            setIsLoading(true);
            getFilesList(item.HistoryId)
                .then(response => setFiles(response.data?.data || []))
                .catch(err => console.error(err))
                .finally(() => setIsLoading(false));
        }
        onToggle();
    };
    
    const formattedDate = new Date(item.HistoryDate).toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'long', 
        year: 'numeric' 
    });

    const formatAmount = (amount) => {
        if (!amount && amount !== 0) return null;
        
        const num = Number(amount);
        
        if (Number.isInteger(num)) {
            return num.toLocaleString('en-US');
        } else {
            return num.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            });
        }
    };

    const formattedAmount = formatAmount(item.HistoryAmount);

    return (
        <Box 
            w="full" 
            sx={{
                bg: 'var(--history-item-bg)',
                border: '1px solid var(--history-item-border)',
                borderRadius: 'xl',
                overflow: 'hidden'
            }}
        >
            <Flex
                w="full" 
                cursor="pointer" 
                onClick={handleToggle}
                justify="space-between"
                align="center"
                p={4}
                sx={{
                    bg: 'var(--history-header-bg)',
                    transition: 'all 0.2s',
                    _hover: {
                        bg: 'var(--history-header-hover-bg)'
                    }
                }}
            >
                <HStack spacing={3} flex="1">
                    <Flex
                        w={10}
                        h={10}
                        borderRadius="lg"
                        align="center"
                        justify="center"
                        sx={{
                            bg: 'var(--history-icon-bg)',
                            color: 'var(--history-icon-color)'
                        }}
                    >
                        <Icon as={FaCalendarAlt} fontSize="md" />
                    </Flex>
                    <VStack align="start" spacing={0} flex="1">
                        <HStack justify="space-between" w="full" align="baseline">
                            <Text 
                                fontWeight="600" 
                                fontSize="md"
                                sx={{ color: 'var(--history-header-text)' }}
                            >
                                {formattedDate}
                            </Text>
                            {formattedAmount && (
                                <Text 
                                    fontWeight="bold" 
                                    fontSize="sm"
                                    sx={{ 
                                        color: 'var(--history-amount-text)',
                                        bg: 'var(--history-amount-bg)',
                                        px: 2,
                                        py: 1,
                                        borderRadius: 'md'
                                    }}
                                >
                                    {formattedAmount}
                                </Text>
                            )}
                        </HStack>
                        {item.Description && (
                            <Text 
                                fontSize="sm" 
                                sx={{ color: 'var(--history-header-subtext)' }}
                            >
                                {item.Description}
                            </Text>
                        )}
                    </VStack>
                </HStack>
                <HStack spacing={2} ml={4}>
                    {files.length > 0 && (
                        <Badge 
                            sx={{
                                bg: 'var(--history-badge-bg)',
                                color: 'var(--history-badge-text)',
                                fontSize: 'xs',
                                borderRadius: 'full',
                                px: 2,
                                py: 1
                            }}
                        >
                            {files.length} file{files.length !== 1 ? 's' : ''}
                        </Badge>
                    )}
                    <Icon 
                        as={isOpen ? FaChevronUp : FaChevronDown} 
                        sx={{ color: 'var(--history-icon-color)' }} 
                        fontSize="sm"
                    />
                </HStack>
            </Flex>
            
            <Collapse in={isOpen} animateOpacity>
                <Box p={4} sx={{ bg: 'var(--history-content-bg)' }}>
                    {isLoading ? (
                        <VStack spacing={3} py={2}>
                            <Spinner size="sm" sx={{ color: 'var(--primary)' }} />
                            <Text fontSize="sm" sx={{ color: 'var(--text-secondary)' }}>
                                Loading files...
                            </Text>
                        </VStack>
                    ) : (
                        <VStack align="stretch" spacing={3}>
                            {files.length > 0 ? 
                                files.map(file => (
                                    <FileItem key={file.FileId} file={file} />
                                )) : (
                                <Text 
                                    fontSize="sm" 
                                    textAlign="center" 
                                    py={4}
                                    sx={{ color: 'var(--text-secondary)' }}
                                >
                                    No files attached
                                </Text>
                            )}
                        </VStack>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

const ContractCard = ({ contract, index }) => {
    const { isOpen, onToggle } = useDisclosure();
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);

    const handleToggle = async () => {
        if (!isOpen && !historyLoaded && contract.ContractId) {
            setIsLoading(true);
            try {
                const response = await getContractHistory(contract.ContractId);
                setHistory(response.data?.data || []);
                setHistoryLoaded(true);
            } catch (err) {
                console.error("History fetch error:", err);
            } finally {
                setIsLoading(false);
            }
        }
        onToggle();
    };

    return (
        <Box 
            borderRadius="2xl"
            overflow="hidden"
            transition="all 0.3s ease-in-out"
            sx={{ 
                bg: 'var(--contract-card-bg)',
                border: '1px solid var(--contract-card-border)',
                boxShadow: '0 4px 20px var(--contract-card-shadow)',
            }}
            _hover={{
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 32px var(--contract-card-hover-shadow)',
            }}
        >
            <HStack 
                p={6}
                justify="space-between" 
                cursor="pointer" 
                onClick={handleToggle}
                sx={{
                    bg: 'var(--contract-card-header-bg)',
                    borderBottom: isOpen ? '1px solid var(--contract-card-border)' : 'none',
                }}
            >
                <HStack spacing={4}>
                    <Flex
                        w={8}
                        h={8}
                        borderRadius="full"
                        align="center"
                        justify="center"
                        sx={{
                            bg: 'var(--contract-number-bg)',
                            color: 'var(--contract-number-text)',
                            fontWeight: 'bold',
                            fontSize: 'sm'
                        }}
                    >
                        {index + 1}
                    </Flex>
                    <VStack align="start" spacing={1}>
                        <Text 
                            fontWeight="bold" 
                            fontSize="lg"
                            sx={{ color: 'var(--contract-card-text)' }}
                        >
                            {contract.ContractTaskName || `Automation Package ${index + 1}`}
                        </Text>
                        <HStack>
                            <Icon as={FaCheck} sx={{ color: 'var(--contract-status-active)' }} />
                            <Text fontSize="sm" sx={{ color: 'var(--contract-card-subtext)' }}>
                                Active
                            </Text>
                        </HStack>
                    </VStack>
                </HStack>
                <Icon 
                    as={isOpen ? FaChevronUp : FaChevronDown} 
                    boxSize={5} 
                    sx={{ color: 'var(--contract-card-icon)' }} 
                />
            </HStack>

            <Collapse in={isOpen} animateOpacity>
                <Box p={6} sx={{ bg: 'var(--contract-card-content-bg)' }}>
                    {isLoading ? (
                        <VStack spacing={4}>
                            <Skeleton height="80px" w="full" borderRadius="xl" />
                            <Skeleton height="80px" w="full" borderRadius="xl" />
                        </VStack>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {history.length > 0 ? (
                                history.map(item => (
                                    <HistoryItem key={item.HistoryId} item={item} />
                                ))
                            ) : historyLoaded ? (
                                <Box
                                    textAlign="center" 
                                    py={8}
                                    sx={{ 
                                        bg: 'var(--empty-history-bg)',
                                        border: '1px dashed var(--empty-history-border)',
                                        borderRadius: 'xl'
                                    }}
                                >
                                    <Icon 
                                        as={FaHistory} 
                                        sx={{ color: 'var(--empty-history-icon)' }}
                                        fontSize="2xl"
                                        mb={3}
                                    />
                                    <Text sx={{ color: 'var(--empty-history-text)' }}>
                                        No activity history found
                                    </Text>
                                </Box>
                            ) : null}
                        </VStack>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

export default ContractCard;